import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import "./Profile.css";
import api from './api';
import { Modal, Button, Form, Input, message } from "antd";

const Profile = () => {
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit profile modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Change password modal
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    const fetchVendor = async () => {
      try {
        const response = await api.get(`/getVendor/${id}`);
        const vendorData = response.data.vendor || response.data;
        setVendor(vendorData);
      } catch (error) {
        console.error("Error fetching vendor:", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchVendor();
    else setLoading(false);
  }, [id]);

  const showModal = () => {
    form.setFieldsValue(vendor);
    setIsModalOpen(true);
  };

  const handleCancel = () => setIsModalOpen(false);

  const handleUpdate = async (values) => {
    try {
      await api.put(`/editVendor/${vendor.vendorId}`, {
        ...vendor,
        ...values,
      });
      setVendor({ ...vendor, ...values });
      message.success("Profile updated successfully!");
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error updating vendor:", error);
      message.error("Failed to update profile.");
    }
  };

  // ======== Change password modal handlers ========
  const showPasswordModal = () => {
    passwordForm.resetFields();
    setIsPasswordModalOpen(true);
  };
  const handlePasswordCancel = () => setIsPasswordModalOpen(false);

  const handleChangePassword = async (values) => {
  // ✅ Check new & confirm match
  if (values.newPassword !== values.confirmPassword) {
    message.error("New Password and Confirm Password do not match!");
    return;
  }

  try {
    // ✅ Call API with vendorId
    const response = await api.put(`/changeVendorPassword/${vendor._id}`, {
      oldPassword: values.oldPassword,
      newPassword: values.newPassword,
    });

    // ✅ Optional: check response
    if (response.data && response.data.message) {
      message.success(response.data.message || "Password changed successfully!");
    } else {
      message.success("Password changed successfully!");
    }

    // ✅ Close modal
    setIsPasswordModalOpen(false);
  } catch (error) {
    console.error("Error changing password:", error);

    // ✅ Show backend error message if available
    if (error.response && error.response.data && error.response.data.message) {
      message.error(error.response.data.message);
    } else {
      message.error("Failed to change password.");
    }
  }
};

// ✅ Render states
if (loading) {
  return <div className="loading">Loading Vendor Profile...</div>;
}

if (!vendor) {
  return <div className="not-found">❌ Vendor not found.</div>;
}

  return (
    <div>
      <Header />
      <div className="profile-container">
        <div className="profile-card">
          {/* Left Section */}
          <div className="profile-left">
            <div className="profile-image">
              {vendor.vendorName?.charAt(0).toUpperCase()}
            </div>
            <h2>{vendor.vendorName}</h2>
            <p className="vendor-id">Vendor ID: {vendor.vendorId}</p>
            <Button
              type="default"
              ghost
              onClick={showModal}
              style={{
                borderColor: "#fff",
                color: "#fff",
                backgroundColor: "transparent",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              Edit Profile
            </Button>
            <Button
              type="default"
              ghost
              onClick={showPasswordModal}
              style={{
                borderColor: "#fff",
                color: "#fff",
                backgroundColor: "transparent",
                fontWeight: "bold",
                marginLeft: "1.5%",
              }}
            >
              Change Password
            </Button>
          </div>

          {/* Right Section */}
          <div className="profile-right">
            <div className="section">
              <h3>Contact Details</h3>
              <div className="info-grid">
                <div>
                  <p className="label">Mobile no.</p>
                  <p>{vendor.mobile}</p>
                </div>
                <div>
                  <p className="label">Email</p>
                  <p>{vendor.email}</p>
                </div>
              </div>
            </div>

            <div className="section">
              <h3>Location Details</h3>
              <div className="info-grid">
                <div>
                  <p className="label">Address</p>
                  <p>{vendor.vendorAddress}</p>
                </div>
                <div>
                  <p className="label">State</p>
                  <p>{vendor.state}</p>
                </div>
              </div>
            </div>

            <div className="section">
              <h3>Business Details</h3>
              <div className="social-links">
                <div>
                  <p className="label">GSTIN</p>
                  <p>{vendor.gstIn}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        title="Edit Vendor Profile"
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        okText="Update"
      >
        <Form form={form} layout="vertical" onFinish={handleUpdate}>
          <Form.Item name="vendorName" label="Vendor Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="mobile" label="Mobile Number" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="vendorAddress" label="Address">
            <Input />
          </Form.Item>
          <Form.Item name="state" label="State">
            <Input />
          </Form.Item>
          <Form.Item name="gstIn" label="GSTIN">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title="Change Password"
        open={isPasswordModalOpen}
        onCancel={handlePasswordCancel}
        onOk={() => passwordForm.submit()}
        okText="Change"
      >
        <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item
            name="oldPassword"
            label="Current Password"
            rules={[{ required: true, message: "Please enter current password" }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[{ required: true, message: "Please enter new password" }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            rules={[{ required: true, message: "Please confirm password" }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      <Footer />
    </div>
  );
};

export default Profile;
